import {$qsAll} from '../../../../../@shared/ts/dom';
import {type TokenResult, type ConfirmResult} from '../../../../../@type/stripe';
import {type GatewayConfiguration} from '../../../models/GatewayConfiguration';
import {type StripeGateway, type IStripeContext} from '../../../models/stripe';
import {Feature} from '../../../reducers/environmentReducer';
import {PeachPayCustomer} from '../../../reducers/peachPayCustomerReducer';
import {DefaultCart} from '../../../reducers/cartReducer';
import {getLocaleText} from '../../../util/translation';
import {createSetupIntent} from '../setup-intent';
import cardBadgeURL from '../../../../img/badge/card.svg';
import cardImageURL from '../../../../img/marks/credit-card-regular.svg';
import {FeatureFlag} from '../../../../../@type/features';

function initStripeCardMethod(): StripeGateway {
	const config: GatewayConfiguration = {
		gatewayId: 'peachpay_stripe_card',
		name: Feature.metadata<string>(FeatureFlag.STRIPE_CARD_GATEWAY, 'title') ?? getLocaleText('Card'),
		description: '', // Not applicable
		assets: {
			title: {src: cardImageURL},
			badge: {src: cardBadgeURL},
		},
	};

	return {
		config,
		init(context: IStripeContext) {
			const {elements} = context;

			const style = {
				base: {
					color: '#333',
					fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
					fontSmoothing: 'antialiased',
					fontSize: '18px',
					'::placeholder': {
						color: '#999',
					},
				},
				invalid: {
					color: '#fa755a',
					iconColor: '#fa755a',
				},
			};

			const cardElement = elements.create('card', {style, hidePostalCode: false});
			$qsAll('div.pp-pm-saved-option[data-gateway="peachpay_stripe_card"]', $el => {
				$el.innerHTML = '';
			});

			cardElement.mount('#pp-pms-new div.pp-pm-saved-option[data-gateway="peachpay_stripe_card"]');
		},
		async tokenize(context: IStripeContext): Promise<TokenResult> {
			const {stripe, elements} = context;

			const $card = elements.getElement('card');
			if (!$card) {
				return [null, {
					paymentStatus: 'creationfailed',
					note: 'Failed to locate valid card element.',
				}];
			}

			const {paymentMethod, error} = await stripe.createPaymentMethod({
				type: 'card',
				card: $card,
				billing_details: PeachPayCustomer.stripeBillingDetails(),
			});

			if (error) {
				const message = error.message ?? 'Failed to tokenize payment method.';

				return [null, {
					paymentStatus: 'creationfailed',
					note: message,
				}];
			}

			if (!paymentMethod?.id) {
				return [null, {
					paymentStatus: 'creationfailed',
					note: 'Failed to tokenize payment method.',
				}];
			}

			// Check for a cart total of 0. If it's more than zero, we do not need setupIntent.
			// See https://www.notion.so/peachpay/Stripe-3D-Secure-card-payments-sometimes-fail-01fae4e0b8694e3680f8d6f6733770df.
			if (DefaultCart.total() > 0) {
				return [paymentMethod.id, {}];
			}

			const clientSecret = await createSetupIntent('card', paymentMethod.id);
			if (!clientSecret) {
				return [null, {
					paymentStatus: 'creationfailed',
					note: 'Failed to create setup intent.',
				}];
			}

			const {error: confirmError, setupIntent: confirmIntent} = await stripe.confirmCardSetup(clientSecret);
			if (!confirmIntent || confirmError) {
				return [null, {
					paymentStatus: 'creationfailed',
					note: confirmError?.message ?? 'Failed to confirm setup intent.',
				}];
			}

			if (confirmIntent.status !== 'succeeded') {
				return [null, {
					paymentStatus: 'creationfailed',
					note: `Setup intent resulted in invalid status(${confirmIntent.status})`,
				}];
			}

			return [confirmIntent.payment_method as string, {}];
		},
		async confirm(context: IStripeContext, clientSecret: string): Promise<ConfirmResult> {
			const {stripe} = context;
			const {paymentIntent, error} = await stripe.retrievePaymentIntent(clientSecret);

			if (!paymentIntent || error) {
				const message = error?.message ?? 'Failed to retrieve payment intent.';
				return [null, {
					note: message,
				}];
			}

			if (paymentIntent.status === 'succeeded' || paymentIntent.status === 'requires_capture') {
				return ['success', {}];
			}

			if (paymentIntent.status === 'requires_action') {
				const {paymentIntent, error} = await stripe.confirmCardPayment(clientSecret);
				if (!paymentIntent || error) {
					const message = error?.message ?? 'Failed to confirm payment intent.';
					return [null, {
						note: message,
					}];
				}

				if (paymentIntent.status === 'succeeded' || paymentIntent.status === 'requires_capture') {
					return ['success', {}];
				}
			}

			return [null, {
				note: `Failed to confirm payment intent with status(${paymentIntent.status})`,
			}];
		},
	};
}

export {initStripeCardMethod};
